import React from 'react';
import { createIntl, createIntlCache, IntlProvider } from 'react-intl';
import { useDispatch, useSelector } from 'react-redux';
import {
  useLocation,
  useNavigate,
  useOutlet,
  useSearchParams,
} from 'react-router';
import { ConfigProvider, Space, Typography, message } from 'antd';
import { useEffectWithPrev } from '@owl-frontend/components';
import Spinner from '../assets/app-spinner.svg';
import OwlVisionContainer, {
  ErrorType,
} from '../components/OwlVisionLandingContainer/OwlVisionLandingContainer';
import { RouteDefs } from '../components/Routing/Routing';
import { actions as localizationSliceActions } from '../data/localization/logic';
import { actions as sessionSliceActions } from '../data/session/logic';
import type { StoreState } from '../data/store';
import { actions as wsSliceActions } from '../data/ws/logic';
import { EventBusProvider } from './event-bus';

interface Props extends React.PropsWithChildren {
  Router?: React.ComponentType;
}

export const Locale = React.createContext<{
  locale: 'en-us';
  messages: {
    [k: string]: string;
  };
  broadcastMessage(data: {
    key?: string;
    id: string;
    type?: 'info' | 'warning' | 'error' | 'loading' | 'success';
    values?: Record<string, any>;
  }): void;
}>({
  locale: 'en-us',
  messages: {},
  broadcastMessage: () => {
    throw new Error('Locale Context broadcastMessage not hooked up!');
  },
});

export const SessionContext = React.createContext<{
  current?: StoreState['session']['current'];
  features: NonNullable<StoreState['session']['features']>;
}>({
  features: {},
});

const getCookieValue = (cookieName: string) => {
  return (
    document.cookie
      .split(';')
      .map((cookie) => cookie.split('=').map((part) => part.trim()))
      .find(([cookieKey]) => cookieKey === cookieName)?.[1] || null
  );
};

const ContextProvider: React.FC<Props> = () => {
  const outlet = useOutlet();
  const dispatch = useDispatch();
  const location = useLocation();
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const [messageApi, messageContextHolder] = message.useMessage();
  const { locale, messages } = useSelector(
    (s: StoreState) => s['localization']
  );
  const intl = React.useMemo(() => {
    const cache = createIntlCache();
    return createIntl({ locale, messages }, cache);
  }, [locale, messages]);
  const {
    current,
    features,
    actions: {
      current: currentAction,
      features: featuresAction,
      requestToken: requestTokenAction,
      requestLogin: requestLoginAction,
      requestLogout: requestLogoutAction,
    },
  } = useSelector((s: StoreState) => s['session']);

  React.useEffect(() => {
    const authCode = params.get('code');
    const state = params.get('state');

    dispatch(sessionSliceActions.requestToken({ authCode, state }));
    // dispatch once on mount
    // eslint-disable-next-line
  }, []);

  React.useEffect(() => {
    dispatch(localizationSliceActions.fetchMessages({ locale }));
  }, [locale]);

  useEffectWithPrev(
    (prevStatus) => {
      if (prevStatus !== 'pending') {
        return;
      }

      if (requestTokenAction.status === 'fulfilled') {
        dispatch(sessionSliceActions.current());
        const url = new URL(window.location.href);
        // Clear authentication parameters from URL after successful token exchange
        url.searchParams.delete('code');
        url.searchParams.delete('state');
        const newPath = url.pathname + url.search + url.hash;
        navigate(newPath, { replace: true });
      }

      if (requestTokenAction.status === 'rejected') {
        // param first in case we're swapping which tenant we log into
        const tenant = params.get('tenant') ?? getCookieValue('tenant');
        if (tenant) {
          dispatch(
            sessionSliceActions.requestLogin({
              tenantId: tenant,
            })
          );
          return;
        }

        navigate(RouteDefs.Broken, {
          state: {
            error: ErrorType.MissingTenantId,
          },
        });
      }
    },
    [requestTokenAction.status, navigate, params]
  );

  useEffectWithPrev(
    (prevStatus) => {
      if (prevStatus !== 'pending') {
        return;
      }

      if (currentAction.status === 'fulfilled') {
        dispatch(sessionSliceActions.updateLastActive());
        dispatch(sessionSliceActions.features());
        dispatch(wsSliceActions.initializeWebSocket());
        dispatch(sessionSliceActions.pollRefreshToken());

        if (location.pathname === RouteDefs.Broken) {
          navigate(RouteDefs.Claims);
        }
      }

      if (currentAction.status === 'rejected') {
        dispatch(sessionSliceActions.requestLogout());
      }
    },
    [currentAction.status, location, navigate, params]
  );

  if (
    currentAction.status === 'pending' ||
    featuresAction.status === 'pending' ||
    requestLoginAction.status === 'pending' ||
    requestTokenAction.status === 'pending'
  ) {
    return (
      <div className="app-spinner">
        <Space
          direction="vertical"
          style={{ display: 'flex', alignItems: 'center' }}
        >
          <Spinner />
        </Space>
      </div>
    );
  }

  if (requestLogoutAction.status === 'pending') {
    return (
      <div className="app-spinner">
        <Space
          direction="vertical"
          style={{ display: 'flex', alignItems: 'center' }}
        >
          <Spinner />
          <Typography.Text type="secondary">
            {messages['platform.start_up.logout']}
          </Typography.Text>
        </Space>
      </div>
    );
  }

  if (!current || !features) {
    return <OwlVisionContainer />;
  }

  return (
    <IntlProvider
      locale={locale}
      defaultLocale="en"
      messages={messages}
      onError={() => {
        /// noop
      }}
    >
      <Locale.Provider
        value={{
          locale,
          messages,
          broadcastMessage: (data) => {
            const content = intl.formatMessage(
              { id: data.id },
              data.values || {}
            );
            messageApi.destroy(data.key ?? data.id);
            messageApi[data.type ?? 'info']({
              key: data.key ?? data.id,
              content,
            });
          },
        }}
      >
        <ConfigProvider
          theme={{
            hashed: false,
            token: {
              colorPrimary: '#7760D7',
              colorLink: '#7760D7',
              colorText: 'rgba(69, 69, 69, 0.88)',
            },
            components: {
              Card: {
                headerHeight: 50,
              },
              Table: {
                headerSortActiveBg: '#FAFAFA',
                headerBorderRadius: 0,
              },
              Typography: {
                titleMarginTop: 0,
                titleMarginBottom: 0,
              },
            },
          }}
        >
          <EventBusProvider>
            <SessionContext.Provider value={{ current, features }}>
              {outlet}
              {messageContextHolder}
            </SessionContext.Provider>
          </EventBusProvider>
        </ConfigProvider>
      </Locale.Provider>
    </IntlProvider>
  );
};
export default ContextProvider;
